import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  memo,
  useCallback,
  useReducer,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import toolData from '../../assets/data/toolList.json'
import { useMessage } from '../../controls/MessageBox'
import Popup from '../../controls/Popup'
import AuthPopup from '../../controls/AuthPopup'
import { useExecutePdfJobQuery } from '../../store/api/authSlice'
import {
  executeFileUpload,
  startPdfJob,
  cleanEmptyNotification,
} from '../../store/notification/action'
import { login } from '../../store/auth/action'
import { createId } from '../../utils/createId'
import { PopupScreen, MessageType, PopupCat } from '../../utils/enums'
import { getFileFromURL, getJobConfig } from '../../utils/helper'
import { getMimeTypes } from '../../utils/mimeTypes'
import RenderPopup from './RenderPopup'
import { Spacer, BannerBtn } from './styles'
import { downloadFile } from '../../store/viewer/downloadApiSlice'
import { TeamsFxContext } from '../TeamFxContextProvider'

import JobContextProvider from './JobContext'
import { stopHub } from '../../store/hub/hubMiddleware'
import { useTranslation } from '../../i18n/useTranslation'
import {
  initializeState,
  reducerFn,
  resetActionConfig,
  setActionConfig,
  setIsAuthPopup,
  setIsJobLoading,
  setIsValid,
  setScreen,
  setSource,
  setTab,
  setToolObj,
  setToolPopup,
} from './reducer'
function MultiCarousel(props) {
  const filePickerRefs = useRef([])
  const { showMessage } = useMessage()
  const { Form } = props
  const { teamsUserCredential } = useContext(TeamsFxContext)
  const t = useTranslation()
  const [state, dispatchFn] = useReducer(reducerFn, initializeState)

  const [isExecute, setIsExecute] = useState(false)
  const [fileList, setFileList] = useState()
  const [index, setIndex] = useState()
  const [jobInfo, setJobInfo] = useState()
  const [docIdExt, setDocIdExt] = useState(null)
  const [mimeTypes, setMimeTypes] = useState([])
  const [fileExtensions, setFileExtensions] = useState()
  const [maxInputFiles, setMaxInputFiles] = useState()
  const [maxFreeInputFiles, setMaxFreeInputFiles] = useState()
  const [minInputFiles, setMinInputFiles] = useState(1)
  const [accept, setAccept] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [rowsSet, setRowSet] = useState(new Set([]))
  const [inputFileTypes, setInputFileTypes] = useState()
  // const [isValid, setIsValid] = useState(true)

  const { data } = useExecutePdfJobQuery(jobInfo, { skip: !isExecute })

  const dispatch = useDispatch()

  const { connectionId, docIds } = useSelector((state) => state.hub)
  const notificationList = useSelector((state) => state.notification)
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const notification = notificationList.filter(
    (e) => !e?.isNotification && e.toolName === state.toolObj?.toolName
  )
  const notificationStatus = useMemo(
    () => notification[0]?.status !== 'finished',
    [notification]
  )
  const _inputFiles = Array.isArray(notification)
    ? notification
    : [notification]

  useEffect(() => {
    if (state.toolObj && state.toolObj.toolName) {
      const config = getJobConfig(state.toolObj.toolName)
      const { mimeTypes, fileExtensions } = getMimeTypes(config.inputFileTypes)
      setInputFileTypes(config.inputFileTypes)
      setFileExtensions(fileExtensions)
      setMimeTypes(mimeTypes)
      setMaxInputFiles(config.maxInputFiles)
      setMaxFreeInputFiles(config.maxFreeInputFiles)
      setMinInputFiles(config.minInputFiles)
      setAccept(fileExtensions.map((ext) => `.${ext}`).join(','))
      // setState(prev => ({...prev,mimeTypes, fileExtensions }))
    }
  }, [state.toolObj, state.toolObj.toolName])
  const minFilesRequired = _inputFiles?.length >= minInputFiles
  const updateActionConfig = useCallback((config, type) => {
    if (type === 'RESET') {
      dispatchFn(resetActionConfig({}))
      return
    }
    dispatchFn(setActionConfig(config))
  }, [])
  const closeToolPopup = useCallback(() => {
    dispatchFn(setToolPopup(false))
    dispatchFn(setIsAuthPopup(false))
    dispatchFn(setScreen(PopupScreen.Action))
    dispatchFn(setTab(PopupCat.RECENT))
    updateActionConfig(null, 'RESET')
    dispatch(cleanEmptyNotification())

    setRowSet(new Set([]))
    setSelectedRows([])
  }, [dispatch, updateActionConfig])
  const fileUploadHandler = useCallback(
    async (tool, files, id) => {
      try {
        const res = await dispatch(
          executeFileUpload({ tool, files, docIdExt: id })
        )

        if (!res.payload) closeToolPopup()
      } catch (error) {
        closeToolPopup()
      }
    },
    [dispatch, closeToolPopup]
  )

  const executePdfJobHandler = useCallback(
    async (tool, files) => {
      const _inputFiles = notificationList.filter(
        (e) => !e?.isNotification && e.toolName === tool?.toolName
      )

      if (!_inputFiles.length) {
        showMessage('No valid files to process.', MessageType.Warning)
        return
      }

      dispatchFn(setIsJobLoading(true))

      try {
        const jobRes = await dispatch(
          startPdfJob(_inputFiles, state.actionConfig, teamsUserCredential)
        )

        if (jobRes?.status === 'ok') {
          showMessage(
            "Job in progress. You'll be notified when it's done.",
            MessageType.Success
          )
        } else {
          showMessage(
            `Cannot create job. Status: ${jobRes?.status ?? 'unknown'}`,
            MessageType.Error
          )
        }
      } catch (error) {
        showMessage(
          `Error in Job creation: ${error?.message ?? 'Unexpected error'}`,
          MessageType.Error
        )
      } finally {
        dispatchFn(setIsJobLoading(false))
        closeToolPopup()
      }
    },
    [
      dispatch,
      dispatchFn,
      notificationList,
      state.actionConfig,
      teamsUserCredential,
      showMessage,
      closeToolPopup,
    ]
  )
  const openToolPopup = useCallback((currentScreen) => {
    dispatchFn(setScreen(currentScreen))
    dispatchFn(setToolPopup(true))
  }, [])

  const openLoginConsentPopup = useCallback(() => {
    dispatchFn(setIsAuthPopup(true))
  }, [])

  const onFileSelect = useCallback(
    ({
      tool,
      files,
      isDrop,
      currentScreen,
      index,
      redirect = true,
      source,
    }) => {
      if (files) setFileList([...files])
      if (tool) dispatchFn(setToolObj(tool))
      if (index) setIndex(index)

      if (!isAuthenticated) {
        openLoginConsentPopup()
        return
      }
      if(source) dispatchFn(setSource(source))

      if (isDrop) {
        const id = createId()
        setDocIdExt(id)
        fileUploadHandler(tool, files, id)
        if (redirect) openToolPopup(currentScreen)
      } else {
        openToolPopup(currentScreen)
      }
    },
    [isAuthenticated, fileUploadHandler, openToolPopup, openLoginConsentPopup]
  )

  const openStartJobScreen = (tool) => {
    tool && dispatchFn(setToolObj(tool))
    openToolPopup(PopupScreen.StartJob)
  }

  const footerBtnClick = useCallback(
    (screen) => {
      try {
        if (screen === PopupScreen.Action) {
          openToolPopup(PopupScreen.StartJob)
          return
        }

        if (screen === PopupScreen.StartJob) {
          executePdfJobHandler(state.toolObj, fileList)
          return
        }
      } catch (error) {}
    },
    [openToolPopup, executePdfJobHandler, state.toolObj, fileList]
  )

  const SignIn = () => {
    closeToolPopup()
    dispatch(login())
  }
  const handleButtonClick = () => {
    if (
      filePickerRefs.current[index] &&
      filePickerRefs.current[index].current
    ) {
      filePickerRefs.current[index].current.click()
    } else {
      openToolPopup(PopupScreen.Action)
    }
  }
  // console.log(
  //   'min inputFiles bool',
  //   _inputFiles?.length >= minInputFiles,
  //   '\n',
  //   'minInputFiles',
  //   minInputFiles,
  //   '\n',
  //   'files Length',
  //   _inputFiles?.length
  // )
  const oneDriveFileHandler = useCallback(async () => {
    dispatchFn(setIsJobLoading(true))

    const tempFileList = await Promise.all(
      selectedRows.map(async (e) => {
        const _fileUrl = await dispatch(
          downloadFile({
            teamsUserCredential,
            itemId: e.id,
            driveId: e.driveId,
          })
        )

        if (_fileUrl.error) return null

        try {
          const file = await getFileFromURL(_fileUrl.payload, e.name)
          return onFileSelect({
            tool: state.toolObj,
            files: [file],
            isDrop: true,
            currentScreen: PopupScreen.StartJob,
            index: null,
            redirect: false,
          })
        } catch (error) {
          return null
        }
      })
    )

    dispatchFn(setIsJobLoading(false))

    const fileList = tempFileList.filter((e) => e !== null)
    if (fileList.length) openToolPopup(PopupScreen.StartJob)
  }, [
    selectedRows, // used in map
    dispatch, // used to call downloadFile
    teamsUserCredential, // used inside dispatch payload
    onFileSelect, // used for selected files
    state.toolObj, // used inside onFileSelect
    openToolPopup, // called at the end
  ])

  const clearTableSelection = () => {
    setRowSet(new Set([]))
    setSelectedRows([])
  }
  const renderFooter = useCallback(() => {
    if (state.screen === PopupScreen.Action)
      return state.tab === PopupCat.RECENT ? (
        <>
          <Spacer />
          <BannerBtn
            color="secondary"
            disabled={!selectedRows.length}
            onClick={oneDriveFileHandler}
            loading={state.isJobLoading}
          >
            {t('btnContinue')}
          </BannerBtn>
          <BannerBtn
            color="neutral"
            onClick={() => {
              if (!docIds.length) dispatch(stopHub())
              closeToolPopup()
            }}
          >
            {t('btnCancel')}
          </BannerBtn>
        </>
      ) : (
        <>
          <Spacer />
          <BannerBtn
            color="neutral"
            onClick={() => {
              if (!docIds.length) dispatch(stopHub())
              closeToolPopup()
            }}
          >
            {t('btnCancel')}
          </BannerBtn>
        </>
      )

    if (state.screen === PopupScreen.StartJob)
      return (
        <>
          <Spacer />
          <BannerBtn
            color="secondary"
            disabled={notificationStatus || !state.isValid || !minFilesRequired}
            onClick={() => footerBtnClick(state.screen)}
            loading={state.isJobLoading}
          >
            {t('btnStartJob')}
          </BannerBtn>
          <BannerBtn color="neutral" onClick={closeToolPopup}>
            {t('btnCancel')}
          </BannerBtn>
        </>
      )
    return <></>
  }, [
    state.screen,
    state.tab,
    selectedRows.length,
    state.isJobLoading,
    t,
    docIds.length,
    dispatch,
    closeToolPopup,
    notificationStatus,
    state.isValid,
    minFilesRequired,
    footerBtnClick,
    oneDriveFileHandler,
  ])
  if (filePickerRefs.current.length !== toolData.length) {
    filePickerRefs.current = Array(toolData.length)
      .fill()
      .map((_, i) => filePickerRefs.current[i] || React.createRef())
  }
  return (
    <JobContextProvider
      value={{
        accept,
        tool: state.toolObj,
        onFileSelect,
        actionConfig: state.actionConfig,
        docIdExt,
        closeToolPopup,
        openToolPopup,
        updateActionConfig,
        index,
        fileUploadOpen: handleButtonClick,
        selectedRows,
        setSelectedRows,
        inputFileTypes,
        filePickerRefs: filePickerRefs.current,
        rowsSet,
        setRowSet,
        clearTableSelection,
        multiple: maxInputFiles > 1,
        maxFiles: /*user.isPaid instead of false*/ false
          ? maxInputFiles
          : maxFreeInputFiles,
        minFilesRequired,
        openStartJobScreen,
        setIsValid: (payload) => dispatchFn(setIsValid(payload)),
        isValid: state.isValid,
        source: state.source,
      }}
    >
      {state.toolPopup && (
        <Popup
          title={t(`lblTool_${state.toolObj?.toolName}`)}
          onClose={() => {
            if (!docIds.length) dispatch(stopHub())
            closeToolPopup()
          }}
          footer={renderFooter}
        >
          <RenderPopup
            screen={state.screen}
            onClose={closeToolPopup}
            footer={renderFooter}
            tab={state.tab}
            updateTab={(tab) => {
              dispatchFn(setTab(tab))
            }}
          />
        </Popup>
      )}
      {state.isAuthPopup && (
        <AuthPopup onClose={closeToolPopup} SignIn={SignIn} />
      )}

      {Form && <Form />}
    </JobContextProvider>
  )
}

export default memo(MultiCarousel)
